import React, { useState, useEffect, useRef } from 'react';
import { Badge, Dropdown, ListGroup, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToNotifications,
  subscribeToUnreadCount,
  fetchGmailUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead } from
  '../../utils/notifications';
import { formatDistanceToNow } from 'date-fns';
import { FaBell, FaCheckDouble, FaEnvelope } from 'react-icons/fa';
import logger from '../../utils/logger';
import './Notifications.css';

const Notifications = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [gmailUnreadCount, setGmailUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }

    // Subscribe to unread count
    const unsubscribeCount = subscribeToUnreadCount(currentUser.uid, (count) => {
      setUnreadCount(count);
    });

    // Subscribe to notifications
    const unsubscribeNotifications = subscribeToNotifications(
      currentUser.uid,
      (notifs) => {
        setNotifications(notifs);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeCount();
      unsubscribeNotifications();
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setGmailUnreadCount(0);
      return;
    }

    let isMounted = true;

    const loadGmailUnreadCount = async () => {
      try {
        const result = await fetchGmailUnreadCount();
        if (isMounted) {
          setGmailUnreadCount(result.unreadCount || 0);
        }
      } catch (error) {
        logger.warn('Unable to refresh Gmail unread count', { error: error.message });
        if (isMounted) {
          setGmailUnreadCount(0);
        }
      }
    };

    loadGmailUnreadCount();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await markNotificationAsRead(notification.id);
      } catch (error) {
        logger.error('Error marking notification as read:', error);
      }
    }
    setShowDropdown(false);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser?.uid) {
      return;
    }
    try {
      await markAllNotificationsAsRead(currentUser.uid);
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
    case 'CASE_MANAGER_ASSIGNED':
      return 'fa-user-tie';
    case 'HOUSING_NAVIGATOR_ASSIGNED':
      return 'fa-home';
    case 'CLIENT_UPDATE':
      return 'fa-user-edit';
    case 'TASK_ASSIGNED':
      return 'fa-tasks';
    case 'CLIENT_STATUS_CHANGED':
      return 'fa-exchange-alt';
    case 'CLIENT_EXITED':
      return 'fa-sign-out-alt';
    case 'CLIENT_BANNED':
      return 'fa-ban';
    case 'CLIENT_HOUSED':
      return 'fa-home';
    case 'CLIENT_TRANSFERRED':
      return 'fa-arrows-alt';
    case 'CLIENT_REACTIVATED':
      return 'fa-redo';
    case 'NEW_CLIENT_ENROLLED':
      return 'fa-user-plus';
    case 'IMPORTANT_FIELD_UPDATED':
      return 'fa-edit';
    case 'TRAINING_ASSIGNED':
    case 'TRAINING_DEADLINE_REMINDER':
      return 'fa-graduation-cap';
    case 'RENT_LATE':
      return 'fa-exclamation-circle';
    default:
      return 'fa-bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
    case 'CASE_MANAGER_ASSIGNED':
      return 'primary';
    case 'HOUSING_NAVIGATOR_ASSIGNED':
      return 'success';
    case 'CLIENT_UPDATE':
      return 'info';
    case 'TASK_ASSIGNED':
      return 'warning';
    case 'CLIENT_STATUS_CHANGED':
      return 'info';
    case 'CLIENT_EXITED':
      return 'warning';
    case 'CLIENT_BANNED':
      return 'danger';
    case 'CLIENT_HOUSED':
      return 'success';
    case 'CLIENT_TRANSFERRED':
      return 'primary';
    case 'CLIENT_REACTIVATED':
      return 'success';
    case 'NEW_CLIENT_ENROLLED':
      return 'info';
    case 'IMPORTANT_FIELD_UPDATED':
      return 'info';
    case 'TRAINING_ASSIGNED':
    case 'TRAINING_DEADLINE_REMINDER':
      return 'info';
    case 'RENT_LATE':
      return 'warning';
    default:
      return 'secondary';
    }
  };

  if (!currentUser) {
    return null;
  }

  const combinedUnreadCount = unreadCount + gmailUnreadCount;

  return (
    <div className="notifications-container" ref={dropdownRef}>
      <Dropdown show={showDropdown} onToggle={setShowDropdown}>
        <Dropdown.Toggle
          aria-label="Notifications"
          as={Button}
          variant="link"
          className="notifications-toggle"
          id="notifications-dropdown">

          <FaBell className="notifications-icon" />
          {combinedUnreadCount > 0 &&
          <Badge bg="danger" className="notifications-badge">
            {combinedUnreadCount > 99 ? '99+' : combinedUnreadCount}
          </Badge>
          }
        </Dropdown.Toggle>

        <Dropdown.Menu
          align="end"
          className="notifications-dropdown-menu"
          style={{ minWidth: '350px', maxWidth: '400px', maxHeight: '500px', overflowY: 'auto' }}>

          <div className="notifications-header">
            <h6 className="mb-0">Notifications</h6>
            {unreadCount > 0 &&
            <Button
              variant="link"
              size="sm"
              className="mark-all-read-btn"
              onClick={handleMarkAllAsRead}>

              <FaCheckDouble className="me-1" />
                Mark all as read
            </Button>
            }
          </div>

          <Dropdown.Divider />

          <ListGroup variant="flush">
            <ListGroup.Item
              className="gmail-notification-item"
              action
              onClick={() => window.open('https://mail.google.com/mail/u/0/#inbox', '_blank', 'noopener,noreferrer')}
              style={{ cursor: 'pointer' }}>

              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FaEnvelope className="me-2 gmail-icon" />
                  <span>Gmail</span>
                </div>
                <Badge className={`gmail-count-badge ${gmailUnreadCount > 0 ? 'has-unread' : 'no-unread'}`} pill>
                  {gmailUnreadCount}
                </Badge>
              </div>
            </ListGroup.Item>
          </ListGroup>

          <Dropdown.Divider />

          {loading ?
            <div className="text-center p-3">
              <Spinner size="sm" />
            </div> :
            notifications.length === 0 ?
              <div className="text-center p-4 text-muted">
                <FaBell className="mb-2" style={{ fontSize: '2rem', opacity: 0.3 }} />
                <p className="mb-0">No notifications</p>
              </div> :

              <ListGroup variant="flush">
                {notifications.map((notification) =>
                  <ListGroup.Item
                    key={notification.id}
                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                    action
                    onClick={() => handleNotificationClick(notification)}
                    style={{ cursor: 'pointer' }}>

                    <div className="d-flex align-items-start">
                      <div className={`notification-icon-wrapper bg-${getNotificationColor(notification.type)}`}>
                        <i className={`fas ${getNotificationIcon(notification.type)}`}></i>
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1 notification-title">{notification.title}</h6>
                            <p className="mb-1 notification-message">{notification.message}</p>
                            <small className="text-muted">
                              {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                            </small>
                          </div>
                          {!notification.read &&
                    <Badge bg="primary" pill className="ms-2">
                            New
                    </Badge>
                          }
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                )}
              </ListGroup>
          }
        </Dropdown.Menu>
      </Dropdown>
    </div>);

};

export default Notifications;
